import { YesObject } from '@yes.technology/react-toolkit'
import { useSituations } from 'situation/shared'
import useSituationActions from 'situation/shared/hooks/useSituationActions'
import { useStatuses } from 'status/shared'
import fetchYesObjects from 'yesObject/shared/api/fetchYesObjects'
import patchObject from 'yesObject/shared/api/patchObject'
import postObject from 'yesObject/shared/api/postObject'

const idObjectClassStorageUrl = window.urlObjectclassUuid
const idObjectClassDocument = window.idObjectclassDocument

type UseDocumentProps = {
  showLoading?: boolean
}

const useDocument = ({ showLoading = false }: UseDocumentProps = {}) => {
  const statusesStorageUrlArray = useStatuses({
    objectclassUuid: idObjectClassStorageUrl
  })

  const statusesDocumentArray = useStatuses({
    objectclassUuid: idObjectClassDocument
  })

  const situations = useSituations()

  const statusDocument = statusesDocumentArray?.[0]

  const { situationActions } = useSituationActions({
    situations
  })

  const initialSituation = situationActions.find(
    (situationAction) => !situationAction.allowed_origin_situation
  )?.destination_situation?.[0]

  const createOrUpdateDocument = async (obj: {
    url: string
    uuid: string
    content_type: string
    complement: string
    des?: string
  }) => {
    let urlRelation: { url: [{ uuid: string }] } = { url: [{ uuid: '' }] }

    if (obj?.url) {
      const storageUrlSituation = initialSituation?.code || ''
      const storageUrlStatus = statusesStorageUrlArray?.[0]?.code || ''

      if (!storageUrlStatus) {
        throw new Error('StorageUrl status is not available')
      }

      if (!storageUrlSituation) {
        throw new Error('StorageUrl situation is not available')
      }

      const storageUrlResponse = await postObject({
        uuid_Objectclass: idObjectClassStorageUrl,
        uuid_status: storageUrlStatus,
        situation: storageUrlSituation,
        data: {
          des: obj.url
        },
        showLoading
      })

      if (!storageUrlResponse.success || !storageUrlResponse.data) {
        throw new Error('Failed to create StorageUrl')
      }

      const storageUrlObject = storageUrlResponse.data as YesObject
      urlRelation = { url: [{ uuid: storageUrlObject.uuid }] }
    }

    const hasUuid =
      !!obj?.uuid && typeof obj.uuid === 'string' && obj.uuid.trim() !== ''

    if (hasUuid) {
      const objectResponse = await fetchYesObjects({
        filterBody: {
          query: {
            $and: [
              {
                'uuid.des': {
                  $eq: obj.uuid
                }
              }
            ]
          },
          objectclass: idObjectClassDocument,
          orderby: [],
          pagination: {
            offset: 0,
            limit: 1
          }
        },
        showLoading
      })

      const existingObject = objectResponse?.objects?.[0]

      if (!existingObject) {
        throw new Error('Document not found')
      }

      const patchResponse = await patchObject({
        uuid: obj.uuid,
        data: {
          version: existingObject.version,
          content_type: obj.content_type,
          complement: obj.complement,
          ...(obj.des && { des: obj.des }),
          ...urlRelation
        },
        showLoading
      })

      if (!patchResponse.success || !patchResponse.data) {
        throw new Error(patchResponse.message || 'Failed to update document')
      }

      const updatedDocument = patchResponse.data as YesObject

      return {
        document: updatedDocument,
        statusDes: statusesStorageUrlArray?.[0]?.des
      }
    } else {
      const documentStatus = statusDocument

      if (!documentStatus?.uuid) {
        throw new Error('Document status is not available')
      }

      if (!window.activeSituationCode) {
        throw new Error('Document situation is not available')
      }

      const postResponse = await postObject({
        uuid_Objectclass: idObjectClassDocument,
        uuid_status: documentStatus?.uuid,
        situation: window.activeSituationCode,
        data: {
          content_type: obj.content_type,
          complement: obj.complement,
          ...(obj.des && { des: obj.des }),
          ...urlRelation
        },
        showLoading
      })

      if (!postResponse.success || !postResponse.data) {
        throw new Error(postResponse.message || 'Failed to create document')
      }

      const newDocument = postResponse.data as YesObject

      return {
        document: newDocument,
        statusDes: statusDocument?.des
      }
    }
  }

  return {
    createOrUpdateDocument
  }
}

export default useDocument
